import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BehaviorSubject, combineLatest, debounceTime, delay, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { ProductEntityService } from 'src/app/core/services/product.service';
import {
  selectMultiEditEnabled,
  selectOpenHoodLinePanelIds,
  selectUIProductSidebarRightOpened
} from '../../store/product-view/product-view.selectors';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { selectCurrentProjectEntities, selectProjectEntityTree } from '../../store/project-data/project-data.selectors';
import {
  addComponent,
  AmbientLightComponent,
  createDefaultOrthographicCamera,
  createDefaultScene,
  createDefaultV3DRenderer,
  getComponent,
  QModelComponent,
  V3dOrthographicSceneManager,
  Visualize3dComponentFactoryService,
  Visualize3dObject,
  Visualize3dObjectLoadEvent,
  Visualize3dObjectLoadStatus,
  Visualize3dViewerOptions
} from '@magicore/visualize-3d';
import { ActivatedRoute } from '@angular/router';
import { Box2, Box3, Event, Object3D, Object3DEventMap, Vector2, Vector3 } from 'three';
import { ActiconV3dComponentsService } from '../../../visualize3d/v3d-components/acticon-v3d-components.service';
import type { EntityReferenceComponent } from '../../../visualize3d/v3d-components/entity-reference/entity-reference';
import { acticonV3dComponentNames } from '../../../visualize3d/v3d-components/acticon-v3d-component-names';
import { stringToViewPort } from '../../../visualize3d/helpers/v3d-viewpoint-helpers';
import { ActiconVisualize3dComponentProvider } from '../../../visualize3d/services/acticon-visualize3d-component-provider.service';
import { ActiconInteractiveComponentHandler } from '../../../visualize3d/v3d-components/acticon-interactive/acticon-interactive-component-handler';
import { tap, withLatestFrom } from 'rxjs/operators';
import type { Entity, TreeEntity } from '../../../core/models/entity.model';
import { selectCurrent3DViewHoodId } from '../../store/project-data/project-data-3d.selectors';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';
import { Visualize3dUserData } from '@magicore/visualize-3d';
import { VisualEditorObject, VisualEditorState } from 'src/app/core/models/visual-editor-state.model';
import { Position } from 'src/app/core/models/position.model';
import { VisualEditorLineUpdatedEvent } from './models/visual-editor-line-updated-event.model';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { convertFormattedEntities, convertKeysToPascalCase } from 'src/app/core/uitls/json-helper';

@Component({
  selector: 'app-product-visual-editor',
  templateUrl: './product-visual-editor.component.html',
  styleUrls: ['./product-visual-editor.component.scss'],
  standalone: false
})
export class ProductVisualEditorComponent implements OnDestroy {
  @Input() projectEntity: TreeEntity | null;
  @Input() isHoodline: boolean;
  @Input() enableDragAndDrop: boolean;
  @Input() errorHighlightIds: string[];
  @Output() entityClicked = new EventEmitter<string>();
  @Output() objectMoved = new EventEmitter<VisualEditorLineUpdatedEvent>();

  @ViewChild('editor') editor: ElementRef;

  productSidebarRightOpened$: Observable<boolean>;
  lessThanLargeScreen: boolean;
  view = 'Top';
  viewPort = stringToViewPort('Top');

  selectedHoodId = '';
  selectedHoodId$: Observable<string>;
  projectEntity$: Observable<TreeEntity | null>;
  projectEntities: Entity[];
  openHoodLineIds: string[];

  dragAndDropMode: boolean;

  dragAndDropStep = 0.05;

  sceneManager: V3dOrthographicSceneManager;

  wait: boolean;
  moveEventsPerSecond = 20;

  visualEditorState: VisualEditorState = {
    initialState: [],
    newState: []
  };

  selectedEntityPosition: Position;

  private resetCameraOnNextObjectLoad = true;

  private currentObjectSubscription: Subscription;
  private sub: Subscription;
  private entityListSubscription: Subscription;
  private openLineIdSubscription: Subscription;
  private updateObject3DSubscription: Subscription;
  private object3d: Visualize3dObject = null;
  private object3d$ = new Subject<Visualize3dObject>();

  private keydownListener: () => void;
  private outsideClickListener: () => void;

  currentObject3d$: Observable<Visualize3dObject> = this.object3d$.asObservable().pipe(debounceTime(300));

  viewerOptions: Visualize3dViewerOptions = { hasInteractions: true };

  private loading3dModelSubject = new BehaviorSubject(false);
  loading3dModel$ = this.loading3dModelSubject.asObservable().pipe(debounceTime(300));

  constructor(
    private productService: ProductEntityService,
    private store: Store,
    private componentFactoryService: Visualize3dComponentFactoryService,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private acticonV3DComponentService: ActiconV3dComponentsService,
    private acticonV3DComponentProvider: ActiconVisualize3dComponentProvider,
    private acticonInteractiveComponentHandler: ActiconInteractiveComponentHandler,
    breakpointObserver: BreakpointObserver
  ) {
    this.acticonV3DComponentService.tryRegisterCustomComponents();

    this.productSidebarRightOpened$ = this.store.select(selectUIProductSidebarRightOpened);
    breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium]).subscribe(result => {
      if (result.matches) {
        this.lessThanLargeScreen = true;
      }
    });

    this.currentObjectSubscription = this.currentObject3d$.pipe(delay(100)).subscribe(() => {
      this.acticonInteractiveComponentHandler.forceRefresh();
    });

    this.selectedHoodId$ = this.store.select(selectCurrent3DViewHoodId);

    this.projectEntity$ = this.store.select(selectProjectEntityTree);

    const obs: Observable<[string, TreeEntity, boolean]> = combineLatest([
      this.selectedHoodId$,
      this.projectEntity$,
      this.productSidebarRightOpened$
    ]);

    let lastSidebarOpenStatus = true;

    this.sub = obs
      .pipe(
        debounceTime(300),
        switchMap(([hoodId, _, sideBarOpen]) => {
          const sidebarOpenStatusChanged = lastSidebarOpenStatus !== sideBarOpen;
          lastSidebarOpenStatus = sideBarOpen;
          if (sideBarOpen) {
            this.loadStatusChange({ status: Visualize3dObjectLoadStatus.LoadStarted });
            return this.updateObject3D(hoodId, this.view, this.selectedHoodId !== hoodId || sidebarOpenStatusChanged).pipe(
              map(() => hoodId)
            );
          }
          return of(hoodId);
        }),
        tap(hoodId => {
          this.selectedHoodId = hoodId;
          this.acticonInteractiveComponentHandler.forceRefresh();
        })
      )
      .subscribe();

    this.route.queryParamMap.pipe(withLatestFrom(this.store.select(selectMultiEditEnabled))).subscribe(([params, multiEdit]) => {
      if (!multiEdit) {
        const selectedId = params.get('selectedId');
        this.acticonInteractiveComponentHandler.setSelectedEntityIds(selectedId);
      }
    });

    this.entityListSubscription = this.store.select(selectCurrentProjectEntities).subscribe(entities => {
      this.projectEntities = entities;
    });

    this.openLineIdSubscription = this.store.select(selectOpenHoodLinePanelIds).subscribe(lineIds => {
      this.openHoodLineIds = lineIds;
    });

    this.sceneManager = new V3dOrthographicSceneManager(
      createDefaultOrthographicCamera(100, 100),
      createDefaultScene(),
      createDefaultV3DRenderer()
    );

    this.outsideClickListener = this.renderer.listen('window', 'click', (e: Event) => {
      if (!this.editor.nativeElement.contains(e.target) && this.dragAndDropMode) {
        this.cancelDragAndDrop();
      }
    });

    this.keydownListener = this.renderer.listen('document', 'keydown', event => {
      if (this.dragAndDropMode && event.key === 'Escape') {
        this.cancelDragAndDrop();
      }
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.updateObject3DSubscription?.unsubscribe();
    this.currentObjectSubscription?.unsubscribe();
    this.entityListSubscription?.unsubscribe();
    this.sceneManager.dispose();

    if (this.keydownListener) {
      this.keydownListener();
    }

    if (this.outsideClickListener) {
      this.outsideClickListener();
    }
  }

  onChangeProductSidebarRightOpened(event: boolean) {
    this.store.dispatch(ProductViewActions.change_ui_product_sidebar_right_opened({ opened: event }));
  }

  /**
   * Fetches the 3D model of the hood for the given view
   * @param hoodId Hood id
   * @param view ViewPoint as string
   * @param forceViewChange
   * @private
   */
  private updateObject3D(hoodId: string, view: string, forceViewChange = false): Observable<void> {
    if (hoodId === null) {
      this.object3d = null;
      this.object3d$.next(null);
      return of();
    }

    this.updateObject3DSubscription?.unsubscribe();

    this.acticonInteractiveComponentHandler.setLineEditorMode(this.isHoodline);

    const viewChanged = forceViewChange || this.view !== view;

    if (this.isHoodline) {
      return this.productService.getHoodLinePreviewModel(hoodId, view).pipe(
        map(data => {
          const object3d = data.v3dObject;

          // Hood3DModelController returns a formatted json for visualise 3d, but this format is not
          // suitable for use in angular entity state, so we need to convert entity data obtained from controller
          const entityData = (data?.entities['$values'] ?? []) as Entity[];

          const pascalCaseConvertedData = convertKeysToPascalCase(entityData);

          const entities = convertFormattedEntities(pascalCaseConvertedData);

          this.store.dispatch(ProjectDataActions.set_hood_line_entities({ entities }));

          this.createSceneLightingComponents(object3d);

          this.resetCameraOnNextObjectLoad = viewChanged || !this.object3d || hoodId !== this.selectedHoodId;
          this.view = view;
          this.viewPort = stringToViewPort(view);

          this.object3d = object3d;
          return this.object3d$.next(object3d);
        })
      );
    } else {
      return this.productService.getHoodModel(hoodId, view).pipe(
        map(object3d => {
          this.createSceneLightingComponents(object3d);

          this.resetCameraOnNextObjectLoad = viewChanged || !this.object3d || hoodId !== this.selectedHoodId;
          this.view = view;
          this.viewPort = stringToViewPort(view);

          this.object3d = object3d;
          return this.object3d$.next(object3d);
        })
      );
    }
  }

  setViewPort(view: string) {
    this.loadStatusChange({ status: Visualize3dObjectLoadStatus.LoadStarted });
    this.updateObject3D(this.selectedHoodId, view, true).subscribe();
  }

  on3DObjectClick(object3d?: Visualize3dObject) {
    if (this.dragAndDropMode) {
      const selectedIds = this.acticonInteractiveComponentHandler.getSelectedIds();
      const selectedModules = this.getModuleObjects().filter(x => selectedIds.includes(this.getObjectEntityId(x)));

      if (selectedModules.length > 0) {
        this.acticonInteractiveComponentHandler.setSelectedEntityIds([]);
        this.selectedEntityPosition = null;

        this.visualEditorState.newState = this.getModuleObjects().map(obj => ({
          objectName: obj.name,
          entityId: this.getObjectEntityId(obj),
          objectPosition: { x: obj.position.x, y: obj.position.y },
          entityPosition: this.getEntityPosition(this.getObjectEntityId(obj))
        }));
        this.updateLineOverlap();
      }
    } else {
      // User can click on empty space, then the object is undefined
      if (!object3d) {
        return;
      }

      // If the clicked object has an entity reference component, we will navigate to that entity
      const entityComponent = getComponent<EntityReferenceComponent>(object3d, acticonV3dComponentNames.entityReference);
      if (entityComponent) {
        const id = entityComponent.containerEntityId;
        this.entityClicked.emit(id);
      }
    }
  }

  /**
   * Creates components for lighting the Visualize3D scene
   * @param object3d The object to add the lights to - this should be the root Object3D
   * @private
   */
  private createSceneLightingComponents(object3d: Visualize3dObject): void {
    // Create ambient light, otherwise parts of model not hit by lights will be completely black
    this.componentFactoryService.create<AmbientLightComponent>('AmbientLight').subscribe(component => {
      addComponent(object3d, component);
    });

    //
    // Create scene lighting
    const lightDistance = 1_000_000; // Distance of the lights from the origin

    const lightDirections = [
      // All directions where light comes from, from brightest to dimmest
      new Vector3(lightDistance, 0, -lightDistance), // From bottom right
      new Vector3(-lightDistance, 0, lightDistance), // From top left
      new Vector3(0, -lightDistance, lightDistance) // From top back
    ];

    const lightColor = '#FFF';

    const sceneLightComponent = this.acticonV3DComponentProvider.createSceneLightComponent(lightDirections, lightColor);

    addComponent(object3d, sceneLightComponent);
  }

  loadStatusChange(status: Visualize3dObjectLoadEvent) {
    if (status.status === Visualize3dObjectLoadStatus.LoadStarted) {
      this.loading3dModelSubject.next(true);
    } else {
      if (status.status === Visualize3dObjectLoadStatus.Loaded && this.resetCameraOnNextObjectLoad) {
        this.resetCameraOnNextObjectLoad = false;
        this.sceneManager.lookAtObjectFromViewPoint(this.viewPort, this.sceneManager.getRootObject());
      }
      this.loading3dModelSubject.next(false);
    }

    if (!this.isHoodline && status.status === Visualize3dObjectLoadStatus.Loaded) {
      this.checkOverlappingModules();
      this.updateErrorHighlights();
    }

    if (this.isHoodline && Visualize3dObjectLoadStatus.Loaded) {
      this.updateLineOverlap();
      this.updateErrorHighlights();
    }
  }

  checkOverlappingModules() {
    const overlappingModuleIds = this.projectEntities
      .filter(e => e.EntityTypeKey === EntityTypeKey.Message && e.Properties['TextId'].Value === 'GastroFlowModule.Error.ModuleOverlapping')
      .map(x => x.ParentId);

    const highlightedEntityIds = [];

    overlappingModuleIds.forEach(moduleId => {
      const moduleChildrenIds = this.projectEntities.filter(e => e.ParentId === moduleId).map(x => x.Id);

      moduleChildrenIds.forEach(id => highlightedEntityIds.push(id));
      highlightedEntityIds.push(moduleId);
    });

    this.acticonInteractiveComponentHandler.setOverlappingIds(highlightedEntityIds);
  }

  openDragAnDrop() {
    if (this.view !== 'Top') {
      this.setViewPort('Top');
    } else {
      this.sceneManager.lookAtObjectFromViewPoint(this.viewPort, this.sceneManager.getRootObject());
    }

    this.dragAndDropMode = true;
    this.sceneManager.controls.enableRotate = false;

    this.acticonInteractiveComponentHandler.setDragAndDropMode(true);

    this.visualEditorState.initialState = this.getModuleObjects().map(obj => ({
      objectName: obj.name,
      entityId: this.getObjectEntityId(obj),
      objectPosition: { x: obj.position.x, y: obj.position.y },
      entityPosition: this.getEntityPosition(this.getObjectEntityId(obj))
    }));

    const selectedIds = this.acticonInteractiveComponentHandler.getSelectedIds();

    this.selectedEntityPosition = this.visualEditorState.initialState.find(x => selectedIds.includes(x.entityId))?.entityPosition;

    this.visualEditorState.newState = [];
  }

  closeDragAndDrop() {
    this.dragAndDropMode = false;
    this.sceneManager.controls.enableRotate = true;
    this.acticonInteractiveComponentHandler.setDragAndDropMode(false);
    this.acticonInteractiveComponentHandler.setSelectedEntityIds(this.openHoodLineIds);

    if (this.visualEditorState.newState.length > 0) {
      const objectsToUpdate: VisualEditorObject[] = [];

      this.visualEditorState.newState.forEach(object => {
        const previousState = this.visualEditorState.initialState.find(x => x.objectName === object.objectName);

        if (
          previousState &&
          (object.entityPosition.x !== previousState.entityPosition.x || object.entityPosition.y !== previousState.entityPosition.y)
        ) {
          objectsToUpdate.push(object);
        }
      });

      const lineIds = [...new Set(objectsToUpdate.map(x => x.entityId))];

      const updateEvent = {
        updatedLines: lineIds.map(id => ({
          entityId: id,
          position: objectsToUpdate.find(x => x.entityId === id).entityPosition
        }))
      };

      this.objectMoved.emit(updateEvent);
    }
  }

  undoDragAndDrop(resetSelection: boolean = false) {
    const modules = this.getModuleObjects();

    modules.forEach(module => {
      const previousState = this.visualEditorState.initialState.find(x => x.objectName === module.name);

      if (previousState) {
        module.position.set(previousState.objectPosition.x, previousState.objectPosition.y, module.position.z);
      }
    });

    setTimeout(() => {
      this.updateLineOverlap();

      if (resetSelection) {
        this.acticonInteractiveComponentHandler.setSelectedEntityIds(this.openHoodLineIds);
      }
    }, 100);

    this.visualEditorState.newState = [];
  }

  cancelDragAndDrop() {
    this.dragAndDropMode = false;
    this.sceneManager.controls.enableRotate = true;
    this.acticonInteractiveComponentHandler.setDragAndDropMode(false);

    this.undoDragAndDrop();

    setTimeout(() => {
      this.acticonInteractiveComponentHandler.setSelectedEntityIds(this.openHoodLineIds);
    }, 100);
  }

  private getModuleObjects(): Object3D<Object3DEventMap>[] {
    const modules: Object3D<Object3DEventMap>[] = [];

    this.sceneManager.scene.traverse(obj => {
      const obj3d = (obj.userData as Visualize3dUserData).v3dObject;

      if (obj3d && obj3d.name === 'GastroFlowModule') {
        modules.push(obj);
      }
    });

    return modules;
  }

  private getObjectEntityId(obj: Object3D<Object3DEventMap>): string {
    const obj3d = (obj.userData as Visualize3dUserData).v3dObject;

    if (obj3d) {
      const entityRef = getComponent<EntityReferenceComponent>(obj3d, acticonV3dComponentNames.entityReference);

      return entityRef?.containerEntityId ?? '';
    }

    return '';
  }

  private isCorner(obj: Object3D<Object3DEventMap>): boolean {
    const obj3d = (obj.userData as Visualize3dUserData).v3dObject;

    if (obj3d) {
      const qModelRef = getComponent<QModelComponent>(obj3d, 'QModel');

      return (qModelRef?.id ?? '').includes('acticon_gw_panel_corner_3');
    }

    return false;
  }

  private getObjectBox(obj: Object3D<Object3DEventMap>, includeCorners = true): Box2 {
    const childEntities = obj.children.filter(x => this.getObjectEntityId(x) !== '' && (includeCorners || !this.isCorner(x)));

    // Without this, new positions for child boxes arent updated in time.
    // TODO: This is a hack, a better solution may be needed
    new Box3().setFromObject(obj);

    const childEntityBoxes = childEntities.map(obj => new Box3().setFromObject(obj));

    // round to 2 decimals to get the corect step values for the box
    const minX = Math.min(...childEntityBoxes.map(box => parseFloat(box.min.x.toFixed(2))));
    const minY = Math.min(...childEntityBoxes.map(box => parseFloat(box.min.y.toFixed(2))));
    const maxX = Math.max(...childEntityBoxes.map(box => parseFloat(box.max.x.toFixed(2))));
    const maxY = Math.max(...childEntityBoxes.map(box => parseFloat(box.max.y.toFixed(2))));

    return new Box2(new Vector2(this.getStep(minX), this.getStep(minY)), new Vector2(this.getStep(maxX), this.getStep(maxY)));
  }

  private convertToBox2(box: Box3): Box2 {
    return new Box2(
      new Vector2(this.getStep(box.min.x), this.getStep(box.min.y)),
      new Vector2(this.getStep(box.max.x), this.getStep(box.max.y))
    );
  }

  // Get the bottom left point of the object adjusted for rotation, and multiply it by 1000 to get the position values of the entity
  private getObjectPosition(obj: Object3D<Object3DEventMap>): Position {
    const objectBox = this.getObjectBox(obj, false);

    const rotationZ = parseFloat(obj.rotation.z.toFixed(2));

    let bottomLeftCorner: Vector2;

    if (rotationZ === 3.14) {
      //Back
      bottomLeftCorner = objectBox.max;
    } else if (rotationZ === -1.57) {
      //Left
      bottomLeftCorner = new Vector2(objectBox.min.x, objectBox.max.y);
    } else if (rotationZ === 1.57) {
      //Right
      bottomLeftCorner = new Vector2(objectBox.max.x, objectBox.min.y);
    } else {
      //Front
      bottomLeftCorner = objectBox.min;
    }

    const x = parseFloat((bottomLeftCorner.x * 1000).toFixed(2));
    const y = parseFloat((bottomLeftCorner.y * 1000).toFixed(2));

    return { x, y };
  }

  private getEntityPosition(entityId: string) {
    const entityModules = this.getModuleObjects().filter(x => this.getObjectEntityId(x) === entityId);

    return this.getObjectPosition(entityModules[0]);
  }

  private getStep(value: number): number {
    return Math.floor(parseFloat((parseFloat(value.toFixed(2)) / this.dragAndDropStep).toFixed(2))) * this.dragAndDropStep;
  }

  getCoordinates(point: Position): string {
    return `X=${point.x.toFixed()}, Y=${point.y.toFixed()}`;
  }

  // Recursively gets all the child components
  private getObjectComponents(obj: Object3D<Object3DEventMap>): Object3D<Object3DEventMap>[] {
    const result = [];

    const childEntities = obj.children.filter(x => this.getObjectEntityId(x) !== '');

    childEntities.forEach(child => {
      result.push(child);
      this.getObjectComponents(child).forEach(component => {
        result.push(component);
      });
    });

    return result;
  }

  private checkIntersection(module1: Object3D<Object3DEventMap>, module2: Object3D<Object3DEventMap>): boolean {
    // Without this, new positions for child boxes arent updated in time.
    new Box3().setFromObject(module1);
    new Box3().setFromObject(module2);

    const childEntities1 = this.getObjectComponents(module1).map(obj => this.convertToBox2(new Box3().setFromObject(obj)));
    const childEntities2 = this.getObjectComponents(module2).map(obj => this.convertToBox2(new Box3().setFromObject(obj)));

    let doesIntersect = false;

    childEntities1.forEach(entity1 => {
      childEntities2.forEach(entity2 => {
        if (this.boxesIntersect(entity1, entity2)) {
          doesIntersect = true;
        }
      });
    });

    return doesIntersect;
  }

  private boxesIntersect(box1: Box2, box2: Box2): boolean {
    return box1.max.x > box2.min.x && box1.max.y > box2.min.y && box2.max.x > box1.min.x && box2.max.y > box1.min.y;
  }

  private getOverlappingLineIds(): string[] {
    const overlappingLineIds = [];

    const modules = this.getModuleObjects();

    modules.forEach(m => {
      modules
        .filter(x => x.id !== m.id)
        .forEach(n => {
          const hasIntersection = this.checkIntersection(m, n);

          if (hasIntersection) {
            overlappingLineIds.push(this.getObjectEntityId(m));
            overlappingLineIds.push(this.getObjectEntityId(n));
          }
        });
    });

    return [...new Set(overlappingLineIds)];
  }

  onPointerMove(event: PointerEvent) {
    if (!this.dragAndDropMode) return;
    if (!this.wait) {
      // fire the event
      this.moveSelectedItem(event);
      // stop any further events
      this.wait = true;
      // after a fraction of a second, allow events again
      setTimeout(() => {
        this.wait = false;
      }, 1000 / this.moveEventsPerSecond);
    }
  }

  private moveSelectedItem(event: PointerEvent) {
    const pointerPosition = this.calculatePointerPosition(event);

    const pointerPosition3d = new Vector3(pointerPosition.x, pointerPosition.y, 0);

    pointerPosition3d.unproject(this.sceneManager.camera).setZ(0);

    const selectedIds = this.acticonInteractiveComponentHandler.getSelectedIds();

    const modules = this.getModuleObjects();

    const selectedModules = modules.filter(x => selectedIds.includes(this.getObjectEntityId(x)));

    if (selectedModules.length > 0) {
      let nonOverlappingModuleIds = modules.map(x => this.getObjectEntityId(x));
      const overlappingModuleIds = [];

      // if multiple modules are selected, position data will be obtained from the first module
      const bottomLeftPosition = selectedModules[0].position;
      const movementVector = new Vector3().subVectors(pointerPosition3d, new Vector3(bottomLeftPosition.x, bottomLeftPosition.y, 0));

      // move each module along the movement vector, which is the difference between mouse pointer and original module position
      selectedModules.forEach(m => {
        const newPosition = m.position.clone().add(movementVector);

        m.position.set(this.getStep(newPosition.x), this.getStep(newPosition.y), m.position.z);

        modules
          .filter(x => x.id !== m.id)
          .forEach(x => {
            const hasIntersection = this.checkIntersection(m, x);

            if (hasIntersection) {
              const id1 = this.getObjectEntityId(m);
              const id2 = this.getObjectEntityId(x);

              overlappingModuleIds.push(id1);
              overlappingModuleIds.push(id2);

              nonOverlappingModuleIds = nonOverlappingModuleIds.filter(i => i !== id1 && i !== id2);
            }
          });
      });

      this.acticonInteractiveComponentHandler.highLightOverlap([...new Set(overlappingModuleIds)]);
      this.acticonInteractiveComponentHandler.unHighLightOverlap([...new Set(nonOverlappingModuleIds)]);

      this.selectedEntityPosition = this.getEntityPosition(this.getObjectEntityId(selectedModules[0]));
    }
  }

  private calculatePointerPosition(pointerEvent: PointerEvent): Vector2 {
    const canvas = this.sceneManager.renderer.domElement;

    const topW = canvas.getBoundingClientRect().left;
    const topH = canvas.getBoundingClientRect().top;
    const x = ((pointerEvent.clientX - topW) / canvas.width) * 2 - 1;
    const y = -((pointerEvent.clientY - topH) / canvas.height) * 2 + 1;
    return new Vector2(x, y);
  }

  private updateLineOverlap() {
    if (this.isHoodline) {
      const overlappingLineIds = this.getOverlappingLineIds();

      this.acticonInteractiveComponentHandler.setOverlappingIds(overlappingLineIds);
      this.store.dispatch(ProductViewActions.set_overlapping_line_ids({ lineIds: overlappingLineIds }));
    }
  }

  private updateErrorHighlights() {
    this.acticonInteractiveComponentHandler.setErrorEntityIds(this.errorHighlightIds ?? []);
  }
}
